import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, Lightbulb, Heart, Moon, Users, BookOpen, Dumbbell, Zap } from "lucide-react";

const Index = () => {
  const neuroplasticityMethods = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Learn New Skills",
      description: "Challenge your brain with new languages, instruments, or hobbies to create fresh neural pathways.",
      image: "https://images.unsplash.com/photo-1543109740-4bdb38fda756?w=400&auto=format&fit=crop&q=80",
      tips: ["Try a new language", "Learn an instrument", "Practice puzzles", "Take up art or crafts"]
    },
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: "Physical Exercise",
      description: "Regular exercise increases BDNF (brain-derived neurotrophic factor) and promotes new neuron growth.",
      image: "https://images.unsplash.com/photo-1591311630200-ffa9120a540f?w=400&auto=format&fit=crop&q=80",
      tips: ["Aerobic exercise", "Strength training", "Yoga and stretching", "Dance or sports"]
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Mindfulness & Meditation",
      description: "Meditation strengthens attention networks and increases gray matter density in key brain regions.",
      image: "https://images.unsplash.com/photo-1543903077-481211f92239?w=400&auto=format&fit=crop&q=80",
      tips: ["Daily meditation", "Breathing exercises", "Mindful walking", "Body scan practice"]
    },
    {
      icon: <Moon className="w-8 h-8" />,
      title: "Quality Sleep",
      description: "Sleep consolidates memories and clears brain toxins, essential for neuroplasticity processes.",
      image: "https://images.unsplash.com/photo-1522587750645-7e0e798b2b59?w=400&auto=format&fit=crop&q=80",
      tips: ["7-9 hours nightly", "Consistent schedule", "Dark, cool room", "No screens before bed"]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Social Engagement",
      description: "Meaningful social connections stimulate cognitive function and emotional resilience.",
      image: "https://images.unsplash.com/photo-1758520705584-3554caf304eb?w=400&auto=format&fit=crop&q=80",
      tips: ["Join communities", "Volunteer work", "Deep conversations", "Collaborative projects"]
    }
  ];

  const benefits = [
    { icon: <Brain className="w-6 h-6" />, text: "Enhanced memory and learning" },
    { icon: <Lightbulb className="w-6 h-6" />, text: "Improved problem-solving" },
    { icon: <Zap className="w-6 h-6" />, text: "Faster cognitive processing" },
    { icon: <Heart className="w-6 h-6" />, text: "Better emotional regulation" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-green-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4">
        <div className="absolute inset-0 bg-[var(--gradient-neural)] opacity-10"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <Brain className="w-20 h-20 text-primary animate-pulse" />
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Developing Neuroplasticity
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Unlock your brain's incredible ability to adapt, learn, and grow throughout your entire life. 
              Discover science-backed methods to enhance cognitive flexibility and mental resilience.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {benefits.map((benefit, index) => (
                <Badge key={index} variant="secondary" className="px-4 py-2 text-sm">
                  <span className="mr-2 text-primary">{benefit.icon}</span>
                  {benefit.text}
                </Badge>
              ))}
            </div>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
              Start Your Journey
            </Button>
          </div>
        </div>
      </section>

      {/* What is Neuroplasticity Section */}
      <section className="py-16 px-4 bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-foreground">What is Neuroplasticity?</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Neuroplasticity is your brain's remarkable ability to reorganize itself by forming new neural connections. 
              This means you can literally rewire your brain to learn new skills, recover from injuries, and adapt to new situations at any age.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1617791160536-598cf32026fb?w=600&auto=format&fit=crop&q=80" 
                alt="Neural network connections" 
                className="rounded-2xl shadow-2xl w-full h-80 object-cover"
              />
            </div>
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl">
                <h3 className="text-2xl font-semibold mb-3 text-primary">Key Principles</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>Neural pathways strengthen with repeated use</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>New experiences create new connections</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>The brain remains changeable throughout life</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>Challenge and novelty drive growth</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-foreground">5 Proven Ways to Boost Neuroplasticity</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Implement these science-backed strategies to enhance your brain's adaptability and unlock your cognitive potential.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {neuroplasticityMethods.map((method, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <div className="relative overflow-hidden rounded-t-lg">
                  <img 
                    src={method.image} 
                    alt={method.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg">
                    <span className="text-primary">{method.icon}</span>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-foreground group-hover:text-primary transition-colors">
                    {method.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {method.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-primary mb-3">Practical Tips:</h4>
                    <ul className="space-y-2">
                      {method.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 bg-accent rounded-full flex-shrink-0"></div>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary via-secondary to-accent">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">
            Start Your Neuroplasticity Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Your brain's potential is limitless. Begin with small, consistent steps and watch as your cognitive abilities flourish.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90 px-8 py-3 text-lg shadow-lg">
              Download Guide
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 py-3 text-lg">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white/90 backdrop-blur-sm border-t">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-muted-foreground">
            © 2024 Neuroplasticity Guide. Empowering minds through science-based brain training.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
